<template lang="html">
  <div
    v-tracking-context="{
      content_element: 'email_application_form',
      id: jobId
    }"
    class="job-application"
  >
    <slot
      :click-track="{
        event_element: eventElementModalClose,
        action_type: 'close'
      }"
    />
    <div v-if="!sentSuccessful" :class="{ processing: isProcessing }">
      <p class="registration-fence__title">
        {{ 'client.job_application.headline' | t }}
      </p>
      <p class="job-application__job-title">
        {{ jobTitle }}
      </p>
      <p class="job-application__job-employer">
        {{ jobEmployer }}
      </p>
      <form @submit.prevent="onSubmit">
        <p class="row job-application__row">
          <label class="job-application__label small-12 medium-3">{{
            'client.job_application.form.salutation' | t
          }}</label>
          <template
            v-for="(salutation, id) in $options.filters.t(
              'client.job_application.form.salutations'
            )"
          >
            <input
              :id="`salutation_id_${id}`"
              :key="`input-${id}`"
              v-model="jobApplication.salutation_id"
              class="custom-input--radio custom-input job-application__radio"
              :value="id"
              type="radio"
            />
            <label
              :key="`label-${id}`"
              v-click-track="{
                event_element: 'application_details',
                action_type: `salutation_id_${id}`
              }"
              :for="`salutation_id_${id}`"
              class="job-application__radio-label"
              >{{ salutation }}</label
            >
          </template>
        </p>

        <p class="row job-application__row">
          <label class="job-application__label medium-3">{{
            'client.job_application.form.title' | t
          }}</label>
          <input
            v-model="jobApplication.title"
            class="job-application__input medium-4"
            type="text"
          />
        </p>

        <p class="row job-application__row">
          <label class="job-application__label medium-3">{{
            'client.job_application.form.first_name' | t
          }}</label>
          <input
            v-model="jobApplication.first_name"
            class="job-application__input medium-9"
            type="text"
          />
        </p>

        <p class="row job-application__row">
          <label class="job-application__label medium-3">{{
            'client.job_application.form.last_name' | t
          }}</label>
          <input
            v-model="jobApplication.last_name"
            class="job-application__input medium-9"
            type="text"
          />
        </p>

        <div class="row job-application__row">
          <label class="job-application__label small-12 medium-3">{{
            'client.job_application.form.email' | t
          }}</label>

          <div class="small-12 medium-9">
            <input
              v-model="jobApplication.email"
              class="job-application__input small-12"
              type="text"
            />
            <p
              v-if="errors && errors.email"
              class="job-application__error-notice"
            >
              {{ errors.email[0] }}
            </p>
          </div>
        </div>

        <p class="row job-application__row">
          <label
            class="medium-9 medium-offset-3 job-application__section-title show-for-medium"
            >{{ 'client.job_application.form.attachments' | t }}</label
          >
        </p>

        <div class="row job-application__row job-application__row--upload">
          <label class="job-application__label small-12 medium-3">{{
            'client.job_application.form.upload' | t
          }}</label>
          <div
            v-tracking-context="{ event_element: 'application_details' }"
            class="small-12 medium-9 job-application__input"
          >
            <file-pond
              ref="pond"
              name="files"
              drop-on-page="true"
              v-bind="translations"
              allow-revert="false"
              allow-multiple="true"
              :max-files="5"
              max-total-file-size="7MB"
              :instant-upload="false"
              :accepted-file-types="acceptedFileTypes"
              :server="serverConfig"
              @processfile="onProcessFile"
              @updatefiles="onUpdateFiles"
              @addfile="onAddFile"
              @removefile="onRemoveFile"
            />
            <p
              v-if="errors && errors.attachments"
              class="job-application__error-notice"
            >
              {{ errors.attachments[0] }}
            </p>

            <p class="job-application__upload-notice">
              {{ translations.upload_file_types }}
            </p>
            <p class="job-application__upload-notice">
              {{ translations.upload_file_size }}
            </p>

            <p
              v-if="errors && errors.general"
              class="job-application__error-notice"
            >
              {{ errors.general[0] }}
            </p>
          </div>
        </div>

        <button
          ref="button"
          :disabled="hasFilePondError || isProcessing"
          class="button button--primary job-application__submit"
        >
          {{ 'client.job_application.form.submit' | t }}
        </button>

        <!-- eslint-disable vue/no-v-html -->
        <p
          v-tracking-context="{
            event_element: 'privacy_notice',
            action_type: 'visit'
          }"
          class="job-application__privacy-notice"
          v-html="
            $options.filters.t('client.job_application.privacy_notice_html')
          "
        />
      </form>
    </div>
    <div v-else>
      <p class="registration-fence__title">
        {{ 'client.job_application.successful.headline' | t }}
      </p>

      <img class="job-application__success-image" :src="paperPlaneSvgPath" />

      <p class="registration-fence__info">
        {{
          'client.job_application.successful.text'
            | t({ email: jobApplication.email })
        }}
      </p>

      <div
        v-tracking-context="{ event_element: 'application_success' }"
        class="flex-container align-center"
      >
        <a
          v-click-track="{ action_type: 'acknowledge' }"
          class="button button--bordered"
          href="#"
          @click.prevent="modalClose"
        >
          <svg-icon icon-name="chevron-left" />
          <span class="button__name-space--small">{{
            'client.job_application.successful.back' | t
          }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
// Import Vue FilePond
import vueFilePond from 'vue-filepond'
import { mapActions, mapMutations, mapState } from 'vuex'
import { Status as filePondStatus } from 'filepond'
import pick from 'lodash-es/pick'

// Import FilePond styles
import 'filepond/dist/filepond.min.css'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'

import { manualTrack } from '@/vue/directives/tracking'
import Api from '@/api'
import paperPlaneSvgPath from 'images/paper_plane.svg'
import uploadFolderSvgPath from 'images/upload_folder.svg'

// Create component
const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize
)

export default {
  components: { FilePond },
  props: {
    applyEmail: {
      type: String,
      default: null
    },
    jobId: {
      type: String,
      default: null
    },
    jobTitle: {
      type: String,
      default: null
    },
    jobEmployer: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      paperPlaneSvgPath,
      uploadFolderSvgPath,
      errors: null,
      isProcessing: false,
      sentSuccessful: false,
      uploadPostObjects: [],
      signedPayload: null,
      jobApplication: {
        email: ''
      },
      filePondFileStatus: null,
      jobApplicationHash: null,
      serverConfig: {
        process: this.process
      },
      acceptedFileTypes: [
        'image/*',
        'text/rtf',
        'text/plain',
        'text/richtext',
        'application/pdf',
        'application/rtf',
        'application/doc',
        'application/x-rtf',
        'application/msword',
        'application/x-soffice',
        'application/vnd.oasis.opendocument.text',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ],
      translations: {
        // default values set by component
        // labelInvalidField: ['Field contains invalid files', Type.STRING],
        // labelFileWaitingForSize: ['Waiting for size', Type.STRING],
        // labelFileSizeNotAvailable: ['Size not available', Type.STRING],
        // labelFileCountSingular: ['file in list', Type.STRING],
        // labelFileCountPlural: ['files in list', Type.STRING],
        // labelFileLoading: ['Loading', Type.STRING],
        // labelFileAdded: ['Added', Type.STRING], // assistive only
        // labelFileLoadError: ['Error during load', Type.STRING],
        // labelFileRemoved: ['Removed', Type.STRING], // assistive only
        // labelFileRemoveError: ['Error during remove', Type.STRING],
        // labelFileProcessing: ['Uploading', Type.STRING],
        // labelFileProcessingComplete: ['Upload complete', Type.STRING],
        // labelFileProcessingAborted: ['Upload cancelled', Type.STRING],
        // labelFileProcessingError: ['Error during upload', Type.STRING],
        // labelFileProcessingRevertError: ['Error during revert', Type.STRING],
        // labelTapToCancel: ['tap to cancel', Type.STRING],
        // labelTapToRetry: ['tap to retry', Type.STRING],
        // labelTapToUndo: ['tap to undo', Type.STRING],
        // labelButtonRemoveItem: ['Remove', Type.STRING],
        // labelButtonAbortItemLoad: ['Abort', Type.STRING],
        // labelButtonRetryItemLoad: ['Retry', Type.STRING],
        // labelButtonAbortItemProcessing: ['Cancel', Type.STRING],
        // labelButtonUndoItemProcessing: ['Undo', Type.STRING],
        // labelButtonRetryItemProcessing: ['Retry', Type.STRING],
        // labelButtonProcessItem: ['Upload', Type.STRING],

        label_file_processing: this.$options.filters.t(
          'client.job_application.upload.label_file_processing'
        ),
        label_file_processing_complete: this.$options.filters.t(
          'client.job_application.upload.label_file_processing_complete'
        ),
        label_file_processing_aborted: this.$options.filters.t(
          'client.job_application.upload.label_file_processing_aborted'
        ),
        label_file_processing_error: this.$options.filters.t(
          'client.job_application.upload.label_file_processing_error'
        ),
        label_tap_to_cancel: this.$options.filters.t(
          'client.job_application.upload.label_tap_to_cancel'
        ),
        label_tap_to_retry: this.$options.filters.t(
          'client.job_application.upload.label_tap_to_retry'
        ),
        upload_file_size: this.$options.filters.t(
          'client.job_application.upload_file_size'
        ),
        upload_file_types: this.$options.filters.t(
          'client.job_application.upload_file_types'
        ),
        label_idle:
          `<img class="job-application__upload-icon" src="${uploadFolderSvgPath}" width="36">` +
          this.$options.filters.t('client.job_application.upload.label_idle'),
        label_max_total_file_size: this.$options.filters.t(
          'client.job_application.upload.label_max_total_file_size'
        ),
        label_max_total_file_size_exceeded: this.$options.filters.t(
          'client.job_application.upload.label_max_total_file_size_exceeded'
        ),
        label_file_type_not_allowed: this.$options.filters.t(
          'client.job_application.upload.label_file_type_not_allowed'
        ),
        file_validate_type_label_expected_types: this.$options.filters.t(
          'client.job_application.upload.file_validate_type_label_expected_types'
        )
      }
    }
  },
  computed: {
    ...mapState(['account']),
    submitData() {
      return {
        ...this.jobApplication,
        job_id: this.jobId,
        signed_payload: this.signedPayload
      }
    },
    hasFilePondError() {
      return this.filePondFileStatus === filePondStatus.ERROR
    },
    eventElementModalClose() {
      return this.sentSuccessful ? 'application_success' : 'button'
    }
  },
  mounted() {
    this.jobApplication = {
      ...this.jobApplication,
      ...((this.account &&
        pick(this.account, [
          'email',
          'salutation_id',
          'title',
          'first_name',
          'last_name'
        ])) ||
        {})
    }
  },
  methods: {
    ...mapActions(['trackGoogleTagManagerEvent']),
    ...mapMutations(['modalClose']),
    genericError() {
      this.isProcessing = false
      this.errors = {
        general: [
          this.$options.filters.t('client.job_application.errors.unspecified')
        ]
      }
      manualTrack(this.$refs.pond.$el, { action_type: 'application_fail' })
    },
    onUpdateFiles(files) {
      this.filePondFileStatus = this.$refs.pond._pond.status
    },
    onProcessFile(file) {
      // invoked by FilePond after each file (attachment) upload;
      // filePondStatus.BUSY while more files pending upload
      if (this.$refs.pond._pond.status === filePondStatus.READY) {
        Api.jobApplicationSend(this.submitData)
          .then(response => {
            this.isProcessing = false
            if (response.status === 'success') {
              this.sentSuccessful = true
              this.clickTrackSuccessStats()
            } else {
              this.resetOnError()
            }
          })
          .catch(() => {
            this.resetOnError()
          })
      }
    },
    onAddFile(evt) {
      // non-descriptive event: match on common morpheme in German and English user message
      if (evt && evt.main && evt.main.match(/max/)) {
        manualTrack(this.$refs.pond.$el, {
          action_type: 'file_add',
          target: 'error-attachment-size-limit'
        })
      } else {
        manualTrack(this.$refs.pond.$el, { action_type: 'file_add' })
      }
    },
    onRemoveFile(_evt) {
      manualTrack(this.$refs.pond.$el, { action_type: 'file_remove' })
    },
    onSubmit() {
      if (this.isProcessing) {
        return
      }

      this.errors = null
      this.isProcessing = true

      Api.jobApplicationFetchUrls(this.fetchUrlsData())
        .then(({ status, uploads }) => {
          if (status === 'ok') {
            this.uploadPostObjects = uploads.uploadPostObjects
            this.signedPayload = uploads.signedPayload
            this.$refs.pond.processFiles().catch(() => {
              this.genericError()
            })
          } else {
            this.genericError()
          }
        })
        .catch(jqXhr => {
          this.isProcessing = false
          if (jqXhr.responseJSON) {
            this.errors = jqXhr.responseJSON.errors
            this.clickTrackFailureReasons(jqXhr.status)
          }
        })
    },
    clickTrackSuccessStats() {
      // all files (attachments) successfully uploaded to bucket
      // and application email emitted by backend after last one
      const textFieldFlags = ['title', 'first_name', 'last_name'].map(
        k => !!this.jobApplication[k]
      )
      const clickTarget = [
        this.jobApplication.salutation_id,
        ...textFieldFlags,
        this.$refs.pond.getFiles().length
      ].join('|')
      manualTrack(this.$refs.button, {
        event_element: 'application_success',
        action_type: 'application_sent',
        target: clickTarget
      })
    },
    clickTrackFailureReasons(statusCode) {
      let clickTarget = [`error-${statusCode}`]
      if (!this.jobApplication.email) {
        clickTarget.push('error-no-sender')
      }
      if (this.$refs.pond.getFiles().length === 0) {
        clickTarget.push('error-no-attachment')
      }
      clickTarget = clickTarget.join('|')
      if (clickTarget) {
        manualTrack(this.$refs.pond.$el, {
          action_type: 'application_fail',
          target: clickTarget
        })
      }
    },
    fetchUrlsData() {
      return {
        ...this.jobApplication,
        attachments: this.$refs.pond.getFiles().map(({ file }) => {
          const { size, name, type } = file
          return { size, name, type }
        })
      }
    },
    resetOnError() {
      this.genericError()
      this.$refs.pond.getFiles().forEach(file => {
        file.abortProcessing()
      })
    },
    process(fieldName, file, metadata, load, error, progress, abort) {
      // fieldName is the name of the input field
      // file is the actual file object to send
      const formData = new FormData()
      const fileIndex = this.$refs.pond
        .getFiles()
        .findIndex(({ file: fileA }) => fileA.name === file.name)
      const uploadPostObject = this.uploadPostObjects[fileIndex]

      uploadPostObject.params.forEach(param => {
        formData.append(param.name, param.value)
      })
      formData.append('file', file, file.name)

      const request = new XMLHttpRequest()
      request.open('POST', uploadPostObject.action)
      // Should call the progress method to update the progress to 100% before calling load
      // Setting computable to false switches the loading indicator to infinite mode
      request.upload.onprogress = e => {
        progress(e.lengthComputable, e.loaded, e.total)
      }
      // Should call the load method when done and pass the returned server file id
      // this server file id is then used later on when reverting or restoring a file
      // so your server knows which file to return without exposing that info to the client
      request.onload = function () {
        if (request.status >= 200 && request.status < 300) {
          // the load method accepts either a string (id) or an object
          load(request.responseText)
        } else {
          // Can call the error method if something is wrong, should exit after
          error('oh no')
        }
      }

      request.onerror = function () {
        error('upload error')
      }

      request.send(formData)

      // Should expose an abort method so the request can be cancelled
      return {
        abort: () => {
          // This function is entered if the user has tapped the cancel button
          request.abort()
          // Let FilePond know the request has been cancelled
          abort()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.processing {
  opacity: 0.5;
  pointer-events: none;
}
</style>
